import { useCallback, useState } from "react";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { MonthPicker, PastMonthBanner } from "@/components/MonthView";
import { FilterBar, SimpleSelect, useRowFilter } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { fmtINR, monthStr } from "@/lib/format";

export default function DeadStock() {
  const [month, setMonth] = useState(monthStr());
  const { data } = useLoad(() => api.get("/report/products", { params: { month } }).then((r) => r.data), [month]);
  const [show, setShow] = useState("dead");
  const extra = useCallback((r) => show === "all" || (show === "dead" ? r.dead : !r.dead), [show]);
  const f = useRowFilter(data?.rows, extra);
  if (!data) return <Loading />;
  const dead = data.rows.filter((r) => r.dead);
  const value = dead.reduce((s, r) => s + r.stock_value, 0);
  return (
    <div>
      <PageHeader title="Dead Stock Check" subtitle={`Products with yearly sales below ${data.settings.dead_stock_threshold} units are flagged as dead stock.`}>
        <MonthPicker month={month} setMonth={setMonth} />
        <ExportButton kind="dead_stock" params={{ month }} />
      </PageHeader>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="panel p-4"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Dead stock SKUs</div><div className="mt-2 font-mono text-3xl font-extrabold text-amber-300" data-testid="dead-count">{dead.length}</div></div>
        <div className="panel p-4"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Value locked</div><div className="mt-2 font-mono text-2xl font-extrabold" data-testid="dead-value">{fmtINR(value)}</div></div>
      </div>
      <PastMonthBanner data={data} />
      <FilterBar f={f} total={data.rows.length}>
        <SimpleSelect value={show} onChange={setShow} testid="dead-filter-select" options={[["dead", "Dead stock only"], ["active", "Active only"], ["all", "All products"]]} />
      </FilterBar>
      <div className="panel overflow-auto max-h-[calc(100vh-360px)]">
        <table className="data-table" data-testid="dead-stock-table">
          <thead><tr><th>Product</th><th>Category</th><th className="text-right">Yearly sales</th><th className="text-right">Current stock</th><th className="text-right">Stock value</th><th>Status</th></tr></thead>
          <tbody>
            {f.filtered.map((r) => (
              <tr key={r.id} data-testid={`dead-row-${r.sku}`}>
                <td className="max-w-[340px]"><div className="truncate font-medium">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div></td>
                <td className="text-xs text-muted-foreground">{r.category}</td>
                <td className="num">{r.yearly_sales}</td><td className="num">{r.current_stock}</td><td className="num">{fmtINR(r.stock_value)}</td>
                <td>{r.dead
                  ? <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Dead stock</span>
                  : <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">Active</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
